import { Coordinate, LightsContainer, ScreenDimensions, TileSize, Visibility } from "game-common/models"
import {
    getLightPolygon,
    getSightPolygon,
    pruneSegments,
    SightPoint,
    VectorLine,
    WallFaceReveal,
} from "game-common/ray_casting"
import { Application, Container, Filter, filters, Graphics, Matrix, Rectangle, RenderTexture, Sprite, Texture } from "pixi.js"
import { ClientGameLogic } from "../../../client_game_logic"
import { logger } from "../../../logger"
import { TranslationComputer } from "./translation_computer"
import { SCALER, ScalingParams } from "../../../client_util"

interface VisionOptions {
    polyMap: Array<VectorLine>
    location: Coordinate
    debug?: Graphics
    visibility?: Visibility
    lights?: LightsContainer
}

// How far a polygon edge may sit inside the true circle it stands in for. A light's boundary used
// to be a fixed 64-gon whatever its size, which is 64 ray targets -- more than every occluder
// around it put together -- to hold a 0.4px error at a torch's radius. Deriving the count from the
// radius instead holds the error where it stops being visible and costs a fraction of the rays.
const CircleFlatness = 0.75

// Never fewer than this, so a tiny light still reads as round, nor more than the old fixed count.
const MinCircleSides = 16
const MaxCircleSides = 64

const circleSides = (radius: number) => {
    if (!(radius > CircleFlatness)) return MinCircleSides
    const sides = Math.ceil(Math.PI / Math.acos(1 - CircleFlatness / radius))
    return Math.min(MaxCircleSides, Math.max(MinCircleSides, sides))
}

// The outdoor light gradient's softening. See the constructor for why it is dialled down.
const BlurRadius = 50
const BlurQuality = 2
const BlurResolution = 0.25

const softBlur = () => {
    const blur = new filters.BlurFilter(BlurRadius, BlurQuality, BlurResolution)
    return blur
}

// drawPolygon's other overload takes Point instances, and flattens them itself. Handing it the
// flat form skips that copy, and is what the sight polygon's vertices are closest to anyway.
const flatten = (polygon: SightPoint[]): number[] => {
    const flat = new Array<number>(polygon.length * 2)
    for (let i = 0; i < polygon.length; i++) {
        flat[i * 2] = polygon[i].x
        flat[i * 2 + 1] = polygon[i].y
    }
    return flat
}

export class ShadowRendering {
    private lastLocation: Coordinate = { x: -1, y: -1 }
    private visibilityTexture: Texture
    private visibilitySprite: Sprite
    private visibilityBounds: Rectangle
    private visibilitySurface: Graphics
    private softLightFilters: Filter[]
    private visibilityLastUpdateTs?: number
    private app: Application
    private surface: Container
    private cleared: boolean = true
    private lightLastChangedTs?: number
    private latestDarkness: number
    private logic: ClientGameLogic
    private roomId: string
    private translationComputer = new TranslationComputer()

    // The mask is baked into ONE render texture, reused for the life of the room.
    //
    // There used to be a Map of up to ten textures keyed by this hash, with a fresh
    // renderer.generateTexture() -- a new GPU texture allocation -- on every miss and a FIFO
    // destroy behind it. It could not hit: the hash carries the player's position at full
    // precision and the stage offset, so every frame the player moves is a unique key, and when
    // the player stands still the hash-unchanged check below returns before the cache is ever
    // consulted. So it allocated and freed a texture per moving frame and served nothing, which
    // on a weak GPU is exactly the alloc/free churn that stalls the driver.
    private needsTextureUpdate: boolean = false
    private cachedVisibilityHash: string = ""
    private visibilityRenderTexture?: RenderTexture
    private bakedWidth = 0
    private bakedHeight = 0
    // Reused for the bake's world->texture translation, to match what generateTexture did.
    private bakeTransform = new Matrix()
    public debugHashCalculation: boolean = false // Debug flag for hash calculation logging

    constructor(app: Application, surface: Container, debug: boolean, logic: ClientGameLogic) {
        this.app = app
        this.surface = surface
        this.logic = logic

        const visibilitySurface = new Graphics()
        // A 50px blur at full resolution and the default four passes is a lot of fill for an
        // effect whose whole job is to be indistinct. Half the passes at a quarter of the
        // resolution is ~8-16x less work and, at this radius, not tellable apart -- the blur is
        // wider than the error it introduces by an order of magnitude. This runs on every rebake,
        // so it is a per-frame cost while the player walks.
        this.softLightFilters = debug ? [] : [softBlur()]
        visibilitySurface.filters = this.softLightFilters
        visibilitySurface.scale.set(ScalingParams.surfaceZoom)
        this.visibilitySurface = visibilitySurface
        this.translationComputer.noDelay = true
    }

    // Shared with the cave minimap; generated once by the world visibility pass.
    sightPolygon: Coordinate[] | null = null
    sightRoomId: string | undefined

    renderVisibility = (visionOptions: VisionOptions): boolean => {
        const shouldRender = this.prepare(visionOptions)
        if (!shouldRender) {
            return false
        }

        // NEW: Only render if we need to update the texture
        if (this.needsTextureUpdate) {
            this.render()
            this.needsTextureUpdate = false
        }

        return true
    }

    private prepare = ({ polyMap, location, visibility, debug, lights }: VisionOptions): boolean => {
        if (!this.logic.tilemapMetaProvider) {
            return false
        }
        const darkness = visibility?.darkness
        const hasDarkness = darkness < 1.0
        const hasPolymap = polyMap?.length > 0
        // Caves took this blur away because blurring the dense polygon mask produced a nearly
        // empty render texture on the live WebGL client. That was the NaN vertices a vertical
        // ray used to emit (see getIntersection): they made the Graphics bounds non-finite, so
        // Pixi computed an empty filter area. With finite bounds the blur is safe everywhere.
        this.visibilitySurface.filters = this.softLightFilters

        if (this.roomId !== this.logic.tilemapMetaProvider?.roomId) {
            this.lastLocation = { x: -1, y: -1 }
            this.translationComputer.update(this.logic.tilemapMetaProvider)
            this.roomId = this.logic.tilemapMetaProvider.roomId
            // NEW: Clear cache on room change
            this.invalidateMask()
        }

        if (!hasPolymap && !hasDarkness && this.cleared === false) {
            this.translationComputer.reset()
            this.visibilitySurface.clear()
            this.cleared = true
            this.surface.mask = undefined
            if (this.visibilitySprite) {
                this.app.stage.removeChild(this.visibilitySprite)
            }
            this.lastLocation = { x: -1, y: -1 }
            // NEW: Clear cache when visibility is disabled
            this.invalidateMask()
            return false
        }

        const lightsChanged = this.lightLastChangedTs !== lights?.lastUpdatedTs
        const playerLocationChanged = !(location.x === this.lastLocation.x && location.y === this.lastLocation.y)
        const darknessChanged = this.latestDarkness !== darkness
        // The mask is baked in stage coordinates. Include the newly exposed areas on all edges
        // and rebake after resizing even when the player and lights have not moved.
        const left = 0 - this.app.stage.x
        const top = 0 - this.app.stage.y / SCALER
        const viewportChanged = !this.visibilityBounds || this.visibilityBounds.x !== left || this.visibilityBounds.y !== top
        if (viewportChanged) {
            this.visibilityBounds = new Rectangle(
                left, top, 27 * TileSize + this.app.stage.x * 2, 16 * TileSize + this.app.stage.y * 2 / SCALER,
            )
        }
        if (this.visibilitySprite) this.visibilitySprite.position.set(left, top)

        // NEW: Calculate visibility hash to determine if update is needed
        const hashState = {
            polyMap,
            location,
            visibility,
            lights,
            playerLocationChanged,
            lightsChanged,
            darknessChanged,
        }

        if (this.debugHashCalculation) {
            this.debugHashCalculationLog(hashState)
        }

        const currentVisibilityHash = this.computeVisibilityHash(hashState)

        if (this.debugHashCalculation) {
            logger.debug(
                { tags: ["shadow", "cache", "debug"] },
                `[Shadow Cache] Current hash: ${currentVisibilityHash}, Cached hash: ${this.cachedVisibilityHash}`,
            )
        }

        // Nothing that shapes the mask has changed, so the texture already on the sprite stands.
        if (currentVisibilityHash === this.cachedVisibilityHash) {
            this.needsTextureUpdate = false // Mark as cache hit
            if (this.debugHashCalculation) {
                logger.debug(
                    { tags: ["shadow", "cache", "hit"] },
                    `[Shadow Cache] Same hash, no update needed: ${currentVisibilityHash}`,
                )
            }
            return true
        }

        // Check if we should skip expensive operations based on old logic
        if (!hasPolymap) {
            // there isn't a poly map. In this case, then we don't care about player location changes,
            // only pure darkness changes
            if (
                !hasDarkness ||
                (this.translationComputer.ready() &&
                    !viewportChanged && !darknessChanged && !playerLocationChanged && !lightsChanged)
            ) {
                return false
            }
        } else {
            // there's a poly map. in this case we care about player changes
            if (!viewportChanged && !playerLocationChanged && !lightsChanged) {
                if (this.visibilityLastUpdateTs === visibility?.lastUpdateTs) {
                    return false
                }
            }
        }

        if (this.visibilityLastUpdateTs !== visibility?.lastUpdateTs) {
            this.visibilityLastUpdateTs = visibility?.lastUpdateTs
        }

        this.lightLastChangedTs = lights.lastUpdatedTs
        this.latestDarkness = darkness
        this.sightPolygon = null
        this.sightRoomId = this.logic.roomId
        this.cleared = false

        const g = this.visibilitySurface

        g.clear()

        const playerLocation = { x: location.x, y: location.y }

        this.translationComputer.compute(playerLocation)
        let { modifierX, modifierY } = this.translationComputer

        if (modifierX === undefined) {
            modifierX = -Math.min(Math.max(playerLocation.x, TileSize * ScalingParams.viewportX),
                (this.logic.tilemapMetaProvider.width() - ScalingParams.viewportX) * TileSize) + 50
        }
        if (modifierY === undefined) {
            const margin = ScalingParams.viewportY - 1
            modifierY = -Math.min(Math.max(playerLocation.y, TileSize * margin),
                (this.logic.tilemapMetaProvider.height() - margin) * TileSize) + 50
        }
        if (modifierX !== undefined) {
            // g.transform.position.x = ((-playerLocation.x + modifierX + ScreenDimensions.w / 2) * 2) - 32 * 11
            g.transform.position.x =
                (modifierX + ScreenDimensions.w / 2) * ScalingParams.surfaceZoom - 48 * ScalingParams.viewportX
        }

        if (modifierY !== undefined) {
            // g.transform.position.y = ((-playerLocation.y + modifierY + ScreenDimensions.h / 2) * 2) - 32 * 7
            g.transform.position.y =
                (modifierY + ScreenDimensions.h / 2) * ScalingParams.surfaceZoom - 48 * ScalingParams.viewportY
        }

        if (hasPolymap) {
            const vs = visibility?.range || 600

            // Top
            polyMap[0] = {
                a: { x: playerLocation.x - vs, y: playerLocation.y - vs },
                b: { x: playerLocation.x + vs, y: playerLocation.y - vs },
            }
            // Bottom
            polyMap[1] = {
                a: { x: playerLocation.x - vs, y: playerLocation.y + vs },
                b: { x: playerLocation.x + vs, y: playerLocation.y + vs },
            }
            // Left
            polyMap[2] = {
                a: { x: playerLocation.x - vs, y: playerLocation.y - vs },
                b: { x: playerLocation.x - vs, y: playerLocation.y + vs },
            }
            // Right
            polyMap[3] = {
                a: { x: playerLocation.x + vs, y: playerLocation.y - vs },
                b: { x: playerLocation.x + vs, y: playerLocation.y + vs },
            }

            const prunedSegments = pruneSegments(polyMap, playerLocation.x, playerLocation.y, vs, 3)
            // A cave's occluders sit on the rock's true tile boundary, so the sightline stops
            // at the surface. Reveal the near half of whatever it stopped against, or the
            // player would stand in a black box with no visible walls.
            const reveal = visibility?.fullOcclusion ? WallFaceReveal : 0

            let polygons = [getSightPolygon(prunedSegments, playerLocation.x, playerLocation.y, reveal)]
            this.sightPolygon = polygons[0]
            if (debug) {
                // debug.transform.position.x = -playerLocation.x + 50 + ScreenDimensions.w / 2
                // debug.transform.position.y = -playerLocation.y + 50 + ScreenDimensions.h / 2

                debug.transform.position.x =
                    (-playerLocation.x + 50 + ScreenDimensions.w / 2) * 2 - 32 * ScalingParams.viewportX
                debug.transform.position.y =
                    (-playerLocation.y + 50 + ScreenDimensions.h / 2) * 2 - 32 * ScalingParams.viewportY

                debug.clear()
                const lineMap = prunedSegments.reduce((acc, x) => {
                    acc.push(x.a)
                    acc.push(x.b)
                    return acc
                }, [])

                debug.beginFill(0xff0000, 0.95)
                for (let point of lineMap) {
                    debug.drawCircle(point.x, point.y, 3)
                }
                debug.endFill()

                debug.beginFill(0x00ff00, debug ? 0.2 : 1)
                for (let polygon of polygons) {
                    for (let point of polygon) {
                        debug.drawCircle(point.x, point.y, 3)
                    }
                }
                debug.endFill()

                debug.beginFill(0xff0000, 0.2)
                for (let polygon of polygons) {
                    debug.drawPolygon(flatten(polygon))
                }
                debug.endFill()
            }

            g.beginFill(0xff0000, darkness)
            for (let polygon of polygons) {
                g.drawPolygon(flatten(polygon))
            }
            g.endFill()
        }

        if (darkness !== undefined) {
            g.beginFill(0xfffffff, hasPolymap ? 0.01 : darkness)
            // for now -- hardcode to large map size
            g.drawRect(
                0,
                0,
                this.logic.tilemapMetaProvider.width() * TileSize,
                this.logic.tilemapMetaProvider.height() * TileSize,
            )
            g.endFill()
        }

        if (darkness !== undefined && lights) {
            lights.iterate(light => {
                const {
                    location: { x, y },
                    intensity,
                    radius,
                } = light
                if (!this.lightCanReachView(playerLocation, x, y, radius)) {
                    return
                }
                g.beginFill(0xffffff, intensity)
                if (hasPolymap && visibility?.fullOcclusion) {
                    // A light must stop at the same cave rock as the player's sight. Drawing an
                    // unrestricted circle here paints over the shadow polygon drawn above.
                    // Its reach is pulled in by the same reveal the polygon is pushed out by,
                    // so the light still reaches exactly `radius` across open ground.
                    const reach = Math.max(1, radius - WallFaceReveal)
                    // Include long walls crossing the light even if both endpoints lie outside it.
                    const blockers = polyMap.slice(4).filter(({ a, b }) =>
                        Math.min(a.x, b.x) <= x + radius && Math.max(a.x, b.x) >= x - radius &&
                        Math.min(a.y, b.y) <= y + radius && Math.max(a.y, b.y) >= y - radius)
                    g.drawPolygon(
                        flatten(getLightPolygon(blockers, x, y, reach, WallFaceReveal, circleSides(reach))),
                    )
                } else {
                    g.drawCircle(x, y, radius)
                }
                g.endFill()
            })
        }

        this.lastLocation.x = location.x
        this.lastLocation.y = location.y

        // NEW: Mark that texture needs update and update cached hash
        this.needsTextureUpdate = true
        this.cachedVisibilityHash = currentVisibilityHash

        if (this.debugHashCalculation) {
            logger.debug(
                { tags: ["shadow", "cache", "miss"] },
                `[Shadow Cache] Cache MISS for hash: ${currentVisibilityHash}`,
            )
        }

        return true
    }

    /**
     * Whether a light could put any brightness inside the baked region, so the ones that cannot
     * are skipped before their sight polygon is cast. Nothing used to cull here at all: every
     * light in the room was shaped against its occluders and drawn, including campfires tens of
     * tiles away contributing not one pixel. That makes the per-frame cost scale with how many
     * lights the ROOM holds rather than how many are on screen.
     *
     * The bound is deliberately loose -- a full viewport of slack in each direction on top of the
     * half that is actually visible -- because the camera is free to sit up to half a viewport off
     * the player near a map edge (see the modifier clamping above), and a light that pops out at
     * the edge of the screen is far worse than one shaded for nothing.
     */
    private lightCanReachView = (center: Coordinate, x: number, y: number, radius: number) => {
        const { viewportX, viewportY, viewportPaddingX, viewportPaddingY } = ScalingParams
        return (
            Math.abs(x - center.x) <= (viewportX * 2 + viewportPaddingX) * TileSize + radius &&
            Math.abs(y - center.y) <= (viewportY * 2 + viewportPaddingY) * TileSize + radius
        )
    }

    private render = () => {
        const bounds = this.visibilityBounds
        // Minimum texture size is 1x1; 0x0 throws.
        const width = Math.max(1, Math.ceil(bounds.width))
        const height = Math.max(1, Math.ceil(bounds.height))

        // Compare against the size WE asked for, not the texture's own: BaseTexture rounds what it
        // is given, so a fractional bounds.width would never equal texture.width and the texture
        // would be thrown away and rebuilt every single frame -- the very churn this replaced.
        if (!this.visibilityRenderTexture || this.bakedWidth !== width || this.bakedHeight !== height) {
            this.visibilityRenderTexture?.destroy(true)
            this.visibilityRenderTexture = RenderTexture.create({ width, height })
            this.bakedWidth = width
            this.bakedHeight = height
        }
        this.visibilityTexture = this.visibilityRenderTexture

        // Same framing generateTexture applied: translate the baked region's top-left to the
        // texture's origin, and let the surface update its own transform since it has no parent.
        this.bakeTransform.tx = -bounds.x
        this.bakeTransform.ty = -bounds.y
        this.app.renderer.render(this.visibilitySurface, {
            renderTexture: this.visibilityRenderTexture,
            // generateTexture could skip this, because its texture was always brand new. Reusing
            // one means last frame's mask is still in it.
            clear: true,
            transform: this.bakeTransform,
            skipUpdateTransform: false,
        })

        if (!this.visibilitySprite) {
            this.visibilitySprite = new Sprite(this.visibilityTexture)
        } else if (this.visibilitySprite.texture !== this.visibilityTexture) {
            this.visibilitySprite.texture = this.visibilityTexture
        }
        this.surface.mask = this.visibilitySprite

        this.visibilitySprite.x = bounds.x
        this.visibilitySprite.y = bounds.y

        this.app.stage.addChild(this.visibilitySprite)
    }

    // --- PiP (follow-cam) light mask ------------------------------------------------------------
    // The main mask (visibilitySprite above) is baked in the PLAYER's screen space, so reusing it for
    // the ember follow-cam — which frames a DIFFERENT entity — makes every light drift across the PiP
    // as the player walks. This builds a SECOND alpha mask, baked in the PiP's OWN screen space: a dim
    // base fill plus brighter "holes" at each light's world position, transformed by the same
    // (cx, cy, zoom) the PiP world render uses (see renderWorldToTexture). Because it shares that
    // framing, each light stays fixed over the world regardless of where the player is.
    //
    // Alignment: the returned sprite is used verbatim as `surface.mask` for the isolated PiP render.
    // It is never parented, so its worldTransform stays identity, which makes the SpriteMaskFilter map
    // mask-texture pixel (X,Y) → render-texture pixel (X,Y) 1:1 (same contract the main mask relies
    // on). We therefore bake the framing into the texture, not the sprite transform.
    private pipDarkness?: Graphics
    private pipMaskTexture?: RenderTexture
    private pipMaskSprite?: Sprite

    renderPipLightMask = (opts: {
        cx: number
        cy: number
        zoom: number
        texW: number
        texH: number
        resolution: number
        visibility?: Visibility
        lights?: LightsContainer
    }): Sprite | undefined => {
        const { cx, cy, zoom, texW, texH, resolution, visibility, lights } = opts
        const darkness = visibility?.darkness
        // No darkness in this room → no mask (the PiP renders at full brightness, same as the world).
        if (darkness === undefined || darkness >= 1.0) {
            return undefined
        }

        if (!this.pipDarkness) {
            this.pipDarkness = new Graphics()
            // Soften the light edges, mirroring the main mask's blur (scaled down for the PiP zoom).
            this.pipDarkness.filters = [new filters.BlurFilter(10)]
        }
        if (
            !this.pipMaskTexture ||
            this.pipMaskTexture.width !== texW ||
            this.pipMaskTexture.height !== texH ||
            this.pipMaskTexture.baseTexture.resolution !== resolution
        ) {
            this.pipMaskTexture?.destroy(true)
            this.pipMaskTexture = RenderTexture.create({ width: texW, height: texH, resolution })
            this.pipMaskSprite = new Sprite(this.pipMaskTexture)
        }

        const g = this.pipDarkness
        g.clear()

        // Base dim fill over just the visible PiP region (a small margin covers the blur bleed at the
        // edges). Bounding it to the view keeps the blur-filter texture small instead of map-sized.
        const halfW = texW / zoom / 2
        const halfH = texH / zoom / 2
        const margin = 48
        g.beginFill(0xffffff, darkness)
        g.drawRect(cx - halfW - margin, cy - halfH - margin, (halfW + margin) * 2, (halfH + margin) * 2)
        g.endFill()

        // Brighter "holes" at each in-view light's world position (matches prepare()'s light pass).
        lights?.iterate(light => {
            const {
                location: { x, y },
                intensity,
                radius,
            } = light
            if (Math.abs(x - cx) > halfW + radius || Math.abs(y - cy) > halfH + radius) {
                return
            }
            g.beginFill(0xffffff, intensity)
            g.drawCircle(x, y, radius)
            g.endFill()
        })

        // Bake the world→PiP-texture framing into the graphics transform (identical to the surface
        // transform in renderWorldToTexture), so the mask lines up pixel-for-pixel with the world.
        g.position.set(texW / 2 - cx * zoom, texH / 2 - cy * zoom)
        g.scale.set(zoom)

        this.app.renderer.render(g, { renderTexture: this.pipMaskTexture, clear: true })

        return this.pipMaskSprite
    }

    // NEW: Compute a hash of the current visibility state
    private computeVisibilityHash(state: {
        polyMap: Array<VectorLine>
        location: Coordinate
        visibility: Visibility
        lights: LightsContainer
        playerLocationChanged: boolean
        lightsChanged: boolean
        darknessChanged: boolean
    }): string {
        const { polyMap, location, visibility, lights } = state

        // Create a hash based on key factors that affect visibility.
        //
        // The location goes in at FULL precision. It used to be quantized to a 32px tile, which is
        // wrong for a mask baked in SCREEN space: prepare() positions the graphics with the camera
        // modifier taken from this exact location, so two positions inside one tile bake to
        // different places. Quantizing them to the same hash froze the mask while the world went on
        // scrolling, and every light -- the player's own and every campfire's -- slid against the
        // ground by however far the camera had moved, then snapped back on the next real bake.
        //
        // It survived locally because the OTHER half of the hash is lights.lastUpdatedTs, and the
        // lights are re-registered by entity_rendering_manager.update(), whose only callers are the
        // server-message handlers. On localhost an update lands on essentially every frame, so the
        // hash changed anyway; at 50ms it lands on ~3 frames in 4, and the rest drew a stale mask.
        // Measured on production: up to 9px of slide per snap in ordinary walking (~20 CSS px at a
        // typical canvas scale), and 50px across a burst of late frames.
        const locationKey = `${location.x}-${location.y}`
        const darknessKey = visibility?.darkness?.toString() || "1.0"
        const lightsKey = lights?.lastUpdatedTs?.toString() || "0"
        const polyMapKey = polyMap?.length?.toString() || "0"
        const visibilityKey = visibility?.lastUpdateTs?.toString() || "0"

        return `${locationKey}-${darknessKey}-${lightsKey}-${polyMapKey}-${visibilityKey}-${this.app.stage.x}-${this.app.stage.y}`
    }

    // Force the next frame to re-bake. The render texture is NOT destroyed: it is still bound to
    // the mask sprite, and freeing it would leave that sprite pointing at a dead texture -- the
    // "Cannot read properties of null (reading 'valid')" crash in Sprite's texture setter if any
    // early return gets between here and the next render(). It is reused in place instead, and
    // only ever destroyed when the baked region's size changes.
    private invalidateMask = () => {
        this.cachedVisibilityHash = ""
        this.needsTextureUpdate = true
    }

    // Public: force the mask to re-bake (resize, debugging).
    forceTextureUpdate = () => {
        this.invalidateMask()
    }

    // Vestigial, kept only so the shadow_* debug satellites that read it keep working; the
    // texture cache they were written to observe is gone, and so is its hit/miss distinction.
    // These and those three files should go together.
    getCacheStats = () => {
        return {
            cacheSize: this.visibilityRenderTexture ? 1 : 0,
            maxCacheSize: 1,
            lastVisibilityHash: this.cachedVisibilityHash,
            needsUpdate: this.needsTextureUpdate,
        }
    }

    // NEW: Debug method to log current state
    debugLog = () => {
        const stats = this.getCacheStats()
        logger.debug(
            { tags: ["shadow", "debug", "cache"] },
            `[Shadow Debug] Cache: ${stats.cacheSize}/${stats.maxCacheSize}, Needs Update: ${stats.needsUpdate}, Hash: ${stats.lastVisibilityHash}`,
        )
    }

    // NEW: Debug method to log hash calculation details
    debugHashCalculationLog = (state: any) => {
        const { polyMap, location, visibility, lights, playerLocationChanged, lightsChanged, darknessChanged } = state

        const locationKey = `${location.x}-${location.y}`
        const darknessKey = visibility?.darkness?.toString() || "1.0"
        const lightsKey = lights?.lastUpdatedTs?.toString() || "0"
        const polyMapKey = polyMap?.length?.toString() || "0"
        const changeFlags = `${playerLocationChanged ? "1" : "0"}${lightsChanged ? "1" : "0"}${
            darknessChanged ? "1" : "0"
        }`

        logger.debug(
            { tags: ["shadow", "debug", "hash"] },
            `[Shadow Hash Debug] Location: ${locationKey}, Darkness: ${darknessKey}, Lights: ${lightsKey}, PolyMap: ${polyMapKey}, Changes: ${changeFlags}`,
        )
    }
}
